import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';
import { planningReadinessBridge } from '../services/planning-readiness-bridge';

/**
 * MENTOR — Zweig ✍️ "Einzelschicht" (Default · 👤 Mitarbeiter · §9.1).
 *
 * Gestartet aus der W1-Eingangsfrage (MentorEntry) via actions[].startTour.
 * "Ich mache vor" (E.1–E.7) → "du machst allein" (E.8, Prinzip 1) →
 * Wiederholungs-Bruecke (E.9 → Muster-Zweig).
 *
 * Reuse der bewaehrten Dialog-Mechanik der (in AP-0 stillgelegten)
 * planner-tutorial-Tour: Dokument-Events `tour:shift-dialog-opened`
 * (work-shifts-planner-timeline) und `tour:shift-saved`
 * (add-employee-in-work-shift) + `dialogContext.attachToDialog`.
 *
 * AP-6 / 6b: E.1–E.9. Die echte, vom `ShiftRepeatPromptService` gerenderte
 * Wiederholungs-Karte (E.9-Anker `ShiftRepeatPromptCard`) + der Uebergang in
 * MentorReunite/MentorEndgame folgen in AP-7 / 6e — bis dahin ist E.9 ein
 * PlannerView-Schritt mit „Ja, Wochenmuster bauen"-Aktion.
 */
function snap() {
  return planningReadinessBridge.snapshot();
}

/**
 * Smartness-Check fuer E.7 (Speichern): Hat der User im offenen Schicht-Dialog
 * KEINE Qualifikation gewaehlt? Das Qualifikations-Feld ist ein `<mat-select>`
 * (Anker `shift-skill-select`); im leeren Zustand zeigt es einen
 * `.mat-mdc-select-placeholder` („Qualifikation auswaehlen"), bei Auswahl
 * stattdessen einen `.mat-mdc-select-value-text` (live verifiziert). Daraus
 * leitet der Save-Schritt einen freundlichen Tipp ab (Tooltip + Voice-Over
 * verzweigen), denn die Qualifikation ist zwar optional, aber die Grundlage
 * fuer die spaetere „wer passt fachlich?"-Erkennung. Defensiv: Ist der Dialog/
 * das Feld nicht da, KEINE Empfehlung (false) — der Normaltext greift.
 */
function shiftHasNoSkill(): boolean {
  if (typeof document === 'undefined') return false;
  const skill = document.querySelector('[onboardingAnchor="shift-skill-select"]');
  if (!skill) return false;
  return !!skill.querySelector('.mat-mdc-select-placeholder, .mat-select-placeholder');
}

/**
 * Smartness-Check fuer E.7: Ist KEIN Mitarbeiter gewaehlt? In diesem Zweig
 * (✍️ Einzelschicht, IMMER eine normale — nicht offene — Schicht) ist der
 * Mitarbeiter ein PFLICHTFELD: ohne ihn bleibt der Speichern-Button
 * ausgegraut (`isSaveDisabled()` in add-employee-in-work-shift.component.ts:
 * `EmployeeId === 0 && !OpenShift`). Der Save-Schritt MUSS das erklaeren,
 * sonst sagt das Voice-Over „klick Speichern", obwohl der Button gar nicht
 * klickbar ist (User-Report 2026-06-17). Deshalb hat dieser Check in E.7
 * VORRANG vor dem optionalen Qualifikations-Tipp. Gleiche DOM-Heuristik wie
 * shiftHasNoSkill (mat-select-Placeholder am `shift-employee-select`).
 * Defensiv: Feld nicht da → false (Normaltext greift).
 */
function shiftHasNoEmployee(): boolean {
  if (typeof document === 'undefined') return false;
  const emp = document.querySelector('[onboardingAnchor="shift-employee-select"]');
  if (!emp) return false;
  return !!emp.querySelector('.mat-mdc-select-placeholder, .mat-select-placeholder');
}

export const MENTOR_SINGLE_SHIFT_TOUR: TourConfig = {
  id: TourFeature.MentorSingleShift,
  version: 1,
  title: 'TUTORIAL.MENTOR.SINGLE.TITLE',
  description: 'TUTORIAL.MENTOR.SINGLE.DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // 👤-Modus erzwingen: der ＋Schicht-Button (E.4) existiert nur im
  // Mitarbeiter-Modus — sonst stockt ein Tour-nach-Tour-Start im ▦-Modus.
  triggerContext: { route: '/planner?view=mitarbeiter' },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: true,
    cooldownHoursAfterDismiss: 72,
    cooldownHoursAfterSnooze: 24,
    suppressAfterCompletion: false,
  },
  priority: 200,
  estimatedDurationSec: 300,
  // feature-discovery = gefuehrter Flow (runTourV2), NICHT help-mode. 'contextual-help'
  // wuerde startTour in die Feld-Icon-Hilfe umleiten (kein Popover). Nicht im
  // Setup-Counter (zaehlt nur category==='setup').
  category: 'feature-discovery',
  renumberVisibleSteps: true,
  launchContext: { route: '/planner?view=mitarbeiter' },
  chapters: [
    {
      id: 'single-shift',
      title: 'TUTORIAL.MENTOR.SINGLE.TITLE',
      steps: [
        {
          // E.1 — Willkommen, klein anfangen ("Teil 1 von 2").
          // Erster Schritt eines verketteten Zweigs → stabiler Container-Anker.
          id: 'e1-welcome',
          anchor: AnchorId.PlannerView,
          title: 'TUTORIAL.MENTOR.SINGLE.E1.TITLE',
          content: 'TUTORIAL.MENTOR.SINGLE.E1.CONTENT',
          placement: 'auto',
          showNextButton: true,
        },
        {
          // E.2 — Der View-Switch (👤/▦), zwei Blicke ein Plan.
          id: 'e2-view-switch',
          anchor: AnchorId.PlannerViewSwitch,
          title: 'TUTORIAL.MENTOR.SINGLE.E2.TITLE',
          content: 'TUTORIAL.MENTOR.SINGLE.E2.CONTENT',
          placement: 'bottom',
          showNextButton: true,
        },
        {
          // E.3 — Wochen-Navigation & Überblick (Abdeckung/Geplant). N=0-aware.
          id: 'e3-week-nav',
          anchor: AnchorId.CalendarNavigation,
          title: 'TUTORIAL.MENTOR.SINGLE.E3.TITLE',
          content: () =>
            snap()?.hasPattern
              ? 'TUTORIAL.MENTOR.SINGLE.E3.CONTENT'
              : 'TUTORIAL.MENTOR.SINGLE.E3.CONTENT_EMPTY',
          placement: 'bottom',
          showNextButton: true,
        },
        {
          // E.4 — ＋ Schicht öffnet den Dialog. FullCalendar propagiert den
          // Klick nicht bis zum Anchor → auf das Dialog-Open-Event lauschen.
          id: 'e4-add-shift',
          anchor: AnchorId.AddShiftButton,
          title: 'TUTORIAL.MENTOR.SINGLE.E4.TITLE',
          content: 'TUTORIAL.MENTOR.SINGLE.E4.CONTENT',
          placement: 'bottom',
          advanceOnDocumentEvent: 'tour:shift-dialog-opened',
          showNextButton: false,
        },
        {
          // E.5 — Im Dialog: Zeiten. Erster Dialog-Schritt → waitForOpen.
          id: 'e5-times',
          anchor: AnchorId.ShiftTimeSection,
          spotlightAnchor: AnchorId.ShiftTimeSection,
          title: 'TUTORIAL.MENTOR.SINGLE.E5.TITLE',
          content: 'TUTORIAL.MENTOR.SINGLE.E5.CONTENT',
          placement: 'right',
          showNextButton: true,
          showPrevButton: false,
          // Härtung: Speichert der User schon hier (statt „Weiter"), rückt die
          // Tour mit dem Speichern weiter — sonst pausiert sie wegen
          // preserveOnClose beim Dialog-Close. Bei Abbruch (kein shift-saved)
          // greift weiterhin die preserveOnClose-Recovery.
          advanceOnDocumentEvent: 'tour:shift-saved',
          dialogContext: {
            waitForOpen: true,
            attachToDialog: true,
            preserveOnClose: true,
          },
        },
        {
          // E.6 — Im Dialog: Wo & wer (Abteilung/Qualifikation/Mitarbeiter).
          id: 'e6-who',
          anchor: AnchorId.ShiftDepartmentSelect,
          title: 'TUTORIAL.MENTOR.SINGLE.E6.TITLE',
          content: 'TUTORIAL.MENTOR.SINGLE.E6.CONTENT',
          placement: 'right',
          showNextButton: true,
          // Härtung (s. E.5): frühes Speichern rückt die Tour weiter statt zu pausieren.
          advanceOnDocumentEvent: 'tour:shift-saved',
          dialogContext: { attachToDialog: true, preserveOnClose: true },
        },
        {
          // E.7 — Speichern = Entwurf. tour:shift-saved feuert erst nach
          // erfolgreicher Persistierung → Tour folgt nur bei echtem Speichern.
          id: 'e7-save',
          anchor: AnchorId.SaveShiftButton,
          title: 'TUTORIAL.MENTOR.SINGLE.E7.TITLE',
          // Smart, mit Vorrang fuer HARTE Sperren: Fehlt der (Pflicht-)Mitarbeiter,
          // ist Speichern ausgegraut → das MUSS zuerst erklaert werden
          // (CONTENT_NEED_EMPLOYEE), sonst sagt das Voice-Over „klick Speichern"
          // bei totem Button. Erst wenn ein Mitarbeiter steht, greift der
          // optionale Qualifikations-Tipp (CONTENT_NO_SKILL). Beide lesen
          // denselben DOM-Check wie ihr jeweiliger Helper. (Die offene-Schicht-
          // Variante CONTENT_OPEN_SHIFT gehoert NICHT hierher — dieser Zweig ist
          // immer eine normale Schicht; ohne Mitarbeiter entsteht KEIN Bedarf,
          // sondern der Button bleibt gesperrt.)
          content: () =>
            shiftHasNoEmployee()
              ? 'TUTORIAL.MENTOR.SINGLE.E7.CONTENT_NEED_EMPLOYEE'
              : shiftHasNoSkill()
                ? 'TUTORIAL.MENTOR.SINGLE.E7.CONTENT_NO_SKILL'
                : 'TUTORIAL.MENTOR.SINGLE.E7.CONTENT',
          audioStepId: () =>
            shiftHasNoEmployee()
              ? 'e7-save-needemployee'
              : shiftHasNoSkill()
                ? 'e7-save-noskill'
                : 'e7-save',
          placement: 'top',
          advanceOnDocumentEvent: 'tour:shift-saved',
          showNextButton: false,
          // Härtung: Wurde schon auf E.5/E.6 gespeichert, ist der Dialog beim
          // Erreichen von E.7 weg → diesen Save-Schritt überspringen (direkt E.8).
          // Im Normalpfad ist der Dialog offen → E.7 wird normal gezeigt.
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="shift-dialog"]'),
          dialogContext: {
            attachToDialog: true,
            autoCloseOnDialogClose: true,
            preserveOnClose: true,
          },
        },
        {
          // E.8 — "Jetzt du" (Prinzip 1). Spotlight weitet sich auf den ganzen
          // Planer; die Tour rückt weiter, sobald der User selbst eine zweite
          // Schicht speichert (tour:shift-saved) — oder per „Weiter" überspringt.
          id: 'e8-you-do',
          anchor: AnchorId.PlannerView,
          title: 'TUTORIAL.MENTOR.SINGLE.E8.TITLE',
          content: 'TUTORIAL.MENTOR.SINGLE.E8.CONTENT',
          placement: 'auto',
          advanceOnDocumentEvent: 'tour:shift-saved',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          // E.9 — Die Brücke: „Brauchst du das jede Woche?" → Muster-Zweig.
          // 6b: PlannerView-Schritt mit Aktion. Die echte, live vom
          // ShiftRepeatPromptService gerenderte Karte (Anker ShiftRepeatPromptCard)
          // wird in AP-7 angedockt.
          //
          // PATTERN-AWARE (User-Report 2026-06-17): Existiert bereits ein
          // Wochenmuster, ist der Pitch „mach daraus ein Muster" sinnlos — der
          // User hat genau das schon getan. Dann KEIN Dead-End (einfaches
          // skipIf wuerde den Endstrang verschlucken), sondern: Text bestätigt
          // das laufende Muster und führt mit EINEM Vorwärts-CTA direkt in den
          // Endstrang (MentorEndgame: Stellen besetzen + freigeben). Ohne Muster
          // bleibt der bisherige Zwei-Wege-Pitch. snap()?.hasPattern speist sich
          // aus den AKTIVEN Vorlagen (siehe planning-readiness-bridge; bei
          // Mentor-Start frisch invalidiert).
          id: 'e9-bridge',
          anchor: AnchorId.PlannerView,
          title: () =>
            snap()?.hasPattern
              ? 'TUTORIAL.MENTOR.SINGLE.E9.TITLE_HAS_PATTERN'
              : 'TUTORIAL.MENTOR.SINGLE.E9.TITLE',
          content: () =>
            snap()?.hasPattern
              ? 'TUTORIAL.MENTOR.SINGLE.E9.CONTENT_HAS_PATTERN'
              : 'TUTORIAL.MENTOR.SINGLE.E9.CONTENT',
          audioStepId: () =>
            snap()?.hasPattern ? 'e9-bridge-haspattern' : 'e9-bridge',
          placement: 'auto',
          showNextButton: false,
          showPrevButton: false,
          actions: [
            {
              // KEIN Muster vorhanden: der klassische Zwei-Wege-Pitch.
              labelKey: 'TUTORIAL.MENTOR.SINGLE.E9.CTA_PATTERN',
              icon: 'fa-solid fa-table-cells',
              startTour: TourFeature.MentorPattern,
              variant: 'primary',
              visibleIf: () => !snap()?.hasPattern,
            },
            {
              // „Nein, einmalig" → direkt in den Endstrang (Freigeben/Abschluss).
              labelKey: 'TUTORIAL.MENTOR.SINGLE.E9.CTA_ONCE',
              icon: 'fa-solid fa-check',
              startTour: TourFeature.MentorEndgame,
              variant: 'secondary',
              visibleIf: () => !snap()?.hasPattern,
            },
            {
              // Muster existiert schon → EIN Vorwärts-CTA in den Endstrang.
              // Kein erneuter Muster-Pitch.
              labelKey: 'TUTORIAL.MENTOR.SINGLE.E9.CTA_CONTINUE',
              icon: 'fa-solid fa-arrow-right',
              startTour: TourFeature.MentorEndgame,
              variant: 'primary',
              visibleIf: () => !!snap()?.hasPattern,
            },
          ],
        },
      ],
    },
  ],
};
